import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/react';
import { signIn, signInWithRedirect, signOut } from 'auth-provider';
import { Button } from 'components/button/Button';
import { DocumentTitle } from 'components/DocumentTitle';
import { TextInput } from 'components/forms/TextInput';
import { sdClient } from 'contexts/SDKClientContext';
import { AppStoreButton } from 'pages/Login/AppStoreButton';
import { EmailScreen } from 'pages/Login/EmailScreen';
import { LoginScreen } from 'pages/Login/LoginScreen';
import { PasswordScreen } from 'pages/Login/PasswordScreen';
import { ResetPasswordEmailScreen } from 'pages/Login/ResetPasswordEmailScreen';
import { useState } from 'react';
import { isAndroid, isIOS, isMobile } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import { BrowserRouter } from 'react-router-dom';
import { ExternalLink } from 'routing/ExternalLink';
import { LoginRoutes } from 'routing/LoginRoutes';
import styled from 'styled-components';
import { SecondaryText } from 'styles/theme_styles/text';
import { tokens } from 'styles/tokens';
import { Environment } from 'utils/EnvironmentHelper';
import { clearPostLoginUrl, setPostLoginUrl } from 'utils/login';
import { adminSessionStash } from 'utils/qaSessionStash';

const cognitoLoginErrors = {
    invalidUserOrPassword: 'Incorrect username or password.',
    passwordAttemptsExceeded: 'Password attempts exceeded',
};

const loginErrors = {
    invalidUserOrPassword: 'The email address and/or password provided is incorrect.',
    passwordAttemptsExceeded:
        'Log in attempts exceeded. Please wait 30 seconds and try again.',
};

export async function logout(global?: boolean) {
    Sentry.setUser(null);
    if (global) {
        await sdClient.logOutUser({});
    }
    await signOut({ global: global ?? false });
    sessionStorage.clear();

    adminSessionStash.clear();
    const allKeys = Object.keys(localStorage);
    const deletableKeys = allKeys.filter(
        (key) => !(key.includes('workbench') || key.includes('global-search')),
    );
    deletableKeys.forEach((key) => localStorage.removeItem(key));

    const { Domain, ClientId } = Environment.cognito;
    const logoutUri = `${window.location.origin}/login`;
    window.location.href = `https://${Domain}/logout?client_id=${ClientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
}

export const Heading = styled.h1`
    font-size: 3.5rem;
    line-height: 1.26; /* 44px / 35px = 1.26 */
    margin-top: 0.8rem;
`;

export const BackButton = styled(Button)`
    font-size: 1.4rem;
    padding: 0;
    font-weight: 500;
    color: ${tokens.text.link};
`;

export const ResetPasswordButton = styled(Button)`
    font-size: 1.4rem;
    padding: 0;
    color: ${tokens.text.link};
`;

export const StyledTextInput = styled(TextInput).attrs({
    appearance: 'rounded',
})`
    background-color: ${tokens.grey.light};
    border-color: ${tokens.nav.border};
    margin-top: 0.5rem;
    font-size: 1.4rem;
`;

export type LoginForm = {
    email: string;
    password?: string;
};

enum Screen {
    Email = 'email',
    Password = 'password',
    SendResetPassword = 'sendResetPassword',
}

const isErrorObj = (err: unknown): err is { name?: string; message?: string } =>
    typeof err === 'object';

/**
 * This is the real implementation of the login, implemented as a function
 * component instead of a class component.
 */
export function MainLogin() {
    const [email, setEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState<string>();
    const [screen, setScreen] = useState<Screen>(Screen.Email);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        reset,
    } = useForm<LoginForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const onSubmitEmail = handleSubmit(async (data: LoginForm) => {
        const res = await sdClient.getAuthProvider({ emailAddress: data.email });
        const authProvider = res.AuthenticationProvider;
        // Use the email from the auth provider result
        // as this will return the proper case-sensitive version
        if (authProvider) {
            // Cognito is the generic identity provider, any other response
            // must be a federated provider which does not use password
            // authentication.
            if (authProvider !== 'Cognito') {
                // Save the current URL for redirect after returning from federated sign in
                setPostLoginUrl();
                signInWithRedirect({ provider: { custom: authProvider } });
            } else {
                setEmail(res.Email);
                setScreen(Screen.Password);
            }
        }
    });

    const onSubmitPassword = handleSubmit(async (data: LoginForm) => {
        if (data.password) {
            try {
                // Clear any post login URL as it is only needed for a federated sign in
                clearPostLoginUrl();
                await signIn({
                    username: data.email.toLowerCase(),
                    password: data.password,
                });

                // Authentication occurs in the background without refreshing,
                // we could use some callback here to trigger the app to rerender
                // but the easiest method is to just refresh the page
                window.location.reload();
            } catch (err) {
                if (isErrorObj(err) && err.name) {
                    if (err.message && err.name === 'NotAuthorizedException') {
                        const msg = err.message;
                        if (msg === cognitoLoginErrors.invalidUserOrPassword) {
                            setErrorMessage(loginErrors.invalidUserOrPassword);
                        } else if (msg === cognitoLoginErrors.passwordAttemptsExceeded) {
                            setErrorMessage(loginErrors.passwordAttemptsExceeded);
                        } else {
                            setErrorMessage(msg);
                        }
                    } else if (err.message) {
                        setErrorMessage(err.message);
                    } else {
                        setErrorMessage('Login failed.');
                    }
                }
            }
        }
    });

    const onBack = () => {
        reset({
            email,
            password: '',
        });
        setErrorMessage('');
        setScreen(Screen.Email);
    };

    const isActionable = (!isSubmitted || isValid) && !(isValidating || isSubmitting);

    return (
        <>
            {screen !== Screen.Email && (
                <div>
                    <BackButton type="button" onClick={onBack}>
                        <FontAwesomeIcon className="mr-10" icon={faArrowLeft} />
                        <strong style={{ overflow: 'hidden', wordBreak: 'break-word' }}>
                            {screen === Screen.Password ? email : 'Back to Log In'}
                        </strong>
                    </BackButton>
                </div>
            )}
            {(screen === Screen.Email || screen === Screen.Password) && (
                <Heading className="mb-40">Log in to SMARTdiagnostics</Heading>
            )}
            {screen === Screen.Email && (
                <EmailScreen
                    register={register}
                    emailErrors={errors.email}
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSubmitEmail();
                    }}
                    onLogin={onSubmitEmail}
                    actionable={isActionable}
                />
            )}
            {screen === Screen.Password && (
                <PasswordScreen
                    register={register}
                    control={control}
                    email={email}
                    errorMessage={errorMessage}
                    passwordErrors={errors.password}
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSubmitPassword();
                    }}
                    onLogin={onSubmitPassword}
                    onResetPassword={() => {
                        setErrorMessage('');
                        setScreen(Screen.SendResetPassword);
                    }}
                    onChange={() => setErrorMessage('')}
                    actionable={isActionable}
                />
            )}
            {screen === Screen.SendResetPassword && (
                <ResetPasswordEmailScreen register={register} email={email} />
            )}
            {screen !== Screen.SendResetPassword && (
                <div
                    className={`flex f-14 ${screen === Screen.Email ? 'pt-40' : 'pt-8'}`}
                >
                    <SecondaryText>Need help?</SecondaryText>
                    <ExternalLink className="ml-5" url="https://support.kcftech.com">
                        Contact Product Support
                    </ExternalLink>
                </div>
            )}
            {isMobile && (
                <div className="pt-24">
                    {isAndroid && <AppStoreButton type="google-play" />}
                    {isIOS && <AppStoreButton type="apple" />}
                </div>
            )}
        </>
    );
}

export function Login() {
    return (
        <>
            <DocumentTitle title="SMARTdiagnostics" />
            <LoginScreen>
                <BrowserRouter>
                    <LoginRoutes />
                </BrowserRouter>
            </LoginScreen>
        </>
    );
}
